import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import map from 'lodash/map';
import { getUserId, getUserRole } from '../userLocalDetails';

export const filterTasksByProgressAndClass = (tasks, progressStatus) =>
  (tasks || []).filter((task) => task.progressStatus === progressStatus);



export const getTasksWithClasses = (filteredTasks, classData) => {
  if (!filteredTasks) return [];

  const classes = classData || [];
  const role = getUserRole();
  const userId = getUserId(); // student id when logged in

  return filteredTasks.flatMap((task) => {
    const matchedClasses = (task.classIds || [task.classId] || []).map((id) =>
      classes.find((cls) => cls.id === id)
    ).filter(Boolean);
    if (role === 'TEACHER') {
      if (matchedClasses.length === 0) {
        return [{ ...task, classTitle: null }];
      }
      return matchedClasses.map((clazz) => ({
        ...task,
        classTitle: clazz.title,
      }));
    } else {
      const studentClasses = matchedClasses
      const titles = studentClasses.map((cls) => cls.title);

      if (titles.length === 0) {
        return []; // not visible to this student
      }

      return [{
        ...task,
        classTitle: titles.join(', '),
      }];
    }
  });
};

export const groupByClass = (tasks) => {
  // Was: chain(tasks).groupBy('id').map(...).value() — but `lodash/chain` only
  // exports the chain *function*, not the wrapper methods (`groupBy`, `map`,
  // etc. are bound at the lodash entry point, not the modular submodule). The
  // chain wrapper crashes with "groupBy is not a function" at runtime. Using
  // standalone helpers keeps the same tree-shake savings (no full lodash) AND
  // avoids the chain-method-binding trap.
  const groups = groupBy(tasks, 'id');
  return Object.entries(groups).map(([id, group]) => ({
    ...omit(group[0], 'classTitle'),
    id,
    classTitles: map(group, 'classTitle').join(','),
  }));
};

export const getDraftTasks = (tasks) => {
  return (tasks || [])?.filter((assignment) => assignment.status === 'DRAFT');
};

export const getAwaitingSubmissions = (tasks) => {
  return (tasks || [])?.filter((assignment) => {
    const dueAtDate = new Date(assignment.dueAt);
    const currentDate = new Date();

    return (
      (assignment.status === 'PUBLISHED' ||
        assignment.status === 'SCHEDULED') &&
      assignment.submissionCount === 0
    );
  })
};

export const getInProgressTasks = (tasks) => {
  return (tasks || [])?.filter((assignment) => {
    const dueAtDate = new Date(assignment.dueAt);
    const currentDate = new Date();

    return (
      assignment.status === 'PUBLISHED' &&
      assignment.submissionCount > 0
    );
  });
};

// True when any task would land in one of the Current Tasks columns
// (In Draft, Assigned, In Review), using the same rules as those columns.
export const hasCurrentTasks = (tasks) =>
  getDraftTasks(tasks).length > 0 ||
  getAwaitingSubmissions(tasks).length > 0 ||
  getInProgressTasks(tasks).length > 0;

// True when any task would land in one of the student's Current Tasks
// columns (Assigned, In Draft, In Review), using the same rules as those
// columns.
export const hasCurrentStudentTasks = (tasks) =>
  ['ASSIGNED', 'DRAFT', 'REVIEW'].some(
    (progressStatus) =>
      filterTasksByProgressAndClass(tasks, progressStatus).length > 0
  );
